/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */
import React, { Suspense, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch } from "react-redux";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { useInjectReducer, useInjectSaga } from "utils/redux-injectors";
import {
  actions,
  reducer as appReducer,
  sliceKey as appSlice,
} from "../app/redux/slice";
import { Auth } from "./containers/Auth/Loadable";
import ForgotPassword from "./containers/Auth/containers/forgotPassword";
import Login from "./containers/Auth/containers/Login/index";
import RecoverySuccess from "./containers/Auth/containers/recoverySuccess";
import ResetPassword from "./containers/Auth/containers/resetPassword";
import Signup from "./containers/Auth/containers/Signup/index";
import VerifyEmail from "./containers/Auth/containers/verifyEmail";
import { authSaga } from "./containers/Auth/redux/saga";
import { authReducer, authSlice } from "./containers/Auth/redux/slice";
import { Features } from "./containers/Features/Loadable";
import Boards from "./containers/Features/containers/Boards";
import Backlog from "./containers/Features/containers/Boards/containers/Backlog";
import BoardView from "./containers/Features/containers/Boards/containers/Tickets";
import Homepage from "./containers/Features/containers/Home";
import { OnBoarding } from "./containers/OnBoarding/Loadable";
import CreateProfile from "./containers/OnBoarding/containers/Profile";
import Setup from "./containers/OnBoarding/containers/Profile/components/setup";
import Welcome from "./containers/OnBoarding/containers/Profile/components/welcome";
import TeamOnBoarding from "./containers/OnBoarding/containers/Team";
import appSaga from "./redux/saga";
import OtpVerify from "./containers/Features/containers/TeamSettings/components/MySetting/OtpVerify";
import TeamSettingRouting from "./containers/Features/containers/TeamSettings/TeamSettingRouting";
import SignUpInvite from "./containers/Auth/containers/SignUpInvite";
import { Sprints } from "./containers/Features/containers/Sprints/Loadable";
import { TeamSetting } from "./containers/Features/containers/TeamSettings/Loadable";

const Threads = React.lazy(
  () => import("app/containers/Features/containers/Threads"),
);

const JoinTeam = React.lazy(
  () =>
    import("./containers/OnBoarding/containers/Profile/components/JoinTeam"),
);
const UserInvitation = React.lazy(
  () => import("./containers/Auth/containers/UserInvitation"),
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <Navigate to="/auth/login" />,
  },
  {
    path: "/auth",
    element: <Auth />,
    children: [
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "forgot-password",
        element: <ForgotPassword />,
      },
      {
        path: "recovery-success",
        element: <RecoverySuccess />,
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "signup",
        element: <Signup />,
      },
      {
        path: "signup-invite",
        element: <SignUpInvite />,
      },
      {
        path: "verify-email",
        element: <VerifyEmail />,
      },
      {
        path: "email-otp-verify",
        element: <OtpVerify />,
      },
      {
        path: "user-invite",
        element: <UserInvitation />,
      },
    ],
  },
  {
    path: "/onboarding",
    element: <OnBoarding />,
    children: [
      {
        path: "create-profile",
        element: <CreateProfile />,
      },
      {
        path: "welcome",
        element: <Welcome />,
      },
      {
        path: "setup",
        element: <Setup />,
      },
      {
        path: "team",
        element: <TeamOnBoarding />,
      },
      {
        path: "join-team",
        element: <JoinTeam />,
      },
    ],
  },
  {
    path: "/app",
    element: <Features />,
    children: [
      {
        path: "home",
        element: <Homepage />,
      },
      {
        path: "threads",
        element: <Threads />,
      },
      {
        path: ":project/:team/sprints",
        element: <Sprints />,
      },
      {
        path: ":project/:team/boards",
        element: <Boards />,
      },
      {
        path: ":project/:team/boards/:boardId/:type/board",
        element: <BoardView />,
      },
      {
        path: ":project/:team/boards/:boardId/backlog",
        element: <Backlog />,
      },
      {
        path: "team",
        element: <TeamSetting />,
        children: TeamSettingRouting,
      },
    ],
  },
]);

export function App() {
  const dispatch = useDispatch();
  const token = localStorage.getItem("accessToken");
  useInjectReducer({
    key: appSlice,
    reducer: appReducer,
  });
  useInjectSaga({ key: appSlice, saga: appSaga });

  useInjectReducer({ key: authSlice, reducer: authReducer });
  useInjectSaga({ key: authSlice, saga: authSaga });

  // update due to mange me request for home state remove form auth state
  useEffect(() => {
    if (token) {
      dispatch(actions.doGetMyProfile());
    }
    return () => {};
  }, [token]);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Helmet titleTemplate="%s - Teambox" defaultTitle="Teambox">
        <meta
          name="description"
          content="Transform Your Software Development"
        />
      </Helmet>
      <RouterProvider router={router} />
    </Suspense>
  );
}
